import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import { SpaceAccountFeatureFlagsModel } from "./SpaceAccountFeatureFlagsModel";
import { SpaceAccountPostHogFeatureFlagIds, SpaceAccountFeatureFlagsModelData } from "./types";
import { computed, makeObservable, runInAction, when } from "mobx";
import { asyncResultModule } from "@/modules/async-result";
import { AsyncResult } from "@/modules/async-result/types";
import { liveQuery, Subscription } from "dexie";
import { BaseError } from "@/domains/errors";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

export class AppStoreSpaceAccountFeatureFlagsStore extends BaseSyncModelStore<
  SpaceAccountFeatureFlagsModel,
  SpaceAccountFeatureFlagsModelData
> {
  private lazyFlags: Record<string, boolean> | undefined;
  private liveQuerySubscription: Subscription | undefined;

  // The AppStore waits on this ready state to set its own ready state.
  // Hence, we block the app from rendering until the flags are loaded.
  public readyState: AsyncResult<boolean> = asyncResultModule.setLoading();

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.SpaceAccountFeatureFlagsConfig, ...injectedDeps });

    makeObservable<
      AppStoreSpaceAccountFeatureFlagsStore,
      | "lazyFlags"
      | "waitForSyncAndSetFlags"
      | "setupReadyStateCheck"
      | "startLiveQueryCheck"
      | "liveQuerySubscription"
      | "unsubscribeLiveQuery"
      | "handleError"
    >(this, {
      lazyFlags: true,
      readyState: true,
      waitForSyncAndSetFlags: true,
      createSyncModel: false,
      setupReadyStateCheck: false,
      startLiveQueryCheck: false,
      liveQuerySubscription: false,
      getFlagStatus: false,
      unsubscribeLiveQuery: false,
      handleError: false,
      featureFlags: computed,
    });

    this.setupReadyStateCheck();
  }

  private setupReadyStateCheck(): void {
    // Wait for MemDB to be ready before we can check the table.
    // We use setInterval because this store is constructed before AppStore's
    // makeAutoObservable() runs, so MobX reactivity isn't set up yet.
    const waitForDb = setInterval(() => {
      if (this.store.isMemDbReady) {
        clearInterval(waitForDb);
        this.waitForSyncAndSetFlags();
      }
    }, 100);
  }

  private waitForSyncAndSetFlags() {
    when(
      () => {
        const syncStore = this.store.sync;
        const hasCompletedInitialSync = syncStore?.hasCompletedInitialSync ?? false;

        // If initial sync has completed, check the table
        return hasCompletedInitialSync;
      },
      () => {
        this.startLiveQueryCheck();
      }
    );
  }

  private startLiveQueryCheck() {
    if (this.liveQuerySubscription) return;

    try {
      const startTime = performance.now();
      logger.info({
        message: "[AppStoreSpaceAccountFeatureFlagsStore] Starting liveQuery for feature flags",
      });

      this.liveQuerySubscription = liveQuery(() => this.localTable.limit(1).toArray()).subscribe({
        next: res => {
          const hasFlags = res.length > 0;
          const hasFlagData = res[0]?.model_data?.flags !== undefined;

          if (hasFlags && hasFlagData) {
            const elapsedMs = Math.round(performance.now() - startTime);
            logger.info({
              message: "[AppStoreSpaceAccountFeatureFlagsStore] Feature flags loaded",
              info: { elapsedMs },
            });
            runInAction(() => {
              this.lazyFlags = { ...res[0].model_data.flags };
              this.readyState = asyncResultModule.setReady(true);
            });
            this.unsubscribeLiveQuery();
          }
        },
        error: err => {
          this.unsubscribeLiveQuery();
          logger.error({
            message: "[AppStoreSpaceAccountFeatureFlagsStore] Error in liveQuery",
            info: { err: objectModule.safeErrorAsJson(err) },
          });
          this.handleError(err);
        },
      });
    } catch (error) {
      this.unsubscribeLiveQuery();
      this.handleError(error as Error);
    }
  }

  private unsubscribeLiveQuery(): void {
    if (this.liveQuerySubscription) {
      this.liveQuerySubscription.unsubscribe();
      this.liveQuerySubscription = undefined;
    }
  }

  private handleError(error: Error): void {
    const err =
      error instanceof BaseError
        ? error
        : new BaseError({
            message: "[AppStoreSpaceAccountFeatureFlagsStore] Failed to initialize feature flags",
            info: { originalError: objectModule.safeErrorAsJson(error) },
          });

    logger.error({
      message: "[AppStoreSpaceAccountFeatureFlagsStore] Setting readyState to error",
      info: { err: objectModule.safeErrorAsJson(err) },
    });

    runInAction(() => {
      // We don't set an error on the ready state here because we don't want to
      // block the app from rendering because of Feature Flags.
      this.readyState = asyncResultModule.setReady(true);
    });
  }

  createSyncModel(
    data: SyncUpdateValue<SpaceAccountFeatureFlagsModelData>
  ): SpaceAccountFeatureFlagsModel {
    return new SpaceAccountFeatureFlagsModel({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  // Feature flags are "locked in" after first access by design.
  // They will not change until the user refreshes the page.
  get featureFlags(): Record<string, boolean> {
    return this.lazyFlags ?? {};
  }

  /**
   * Returns the status of a feature flag. Returns `undefined` if the backend doesn't know
   * the status of this flag for this user yet.
   */
  getFlagStatus(flag: SpaceAccountPostHogFeatureFlagIds): boolean | undefined {
    const val = this.featureFlags[flag];
    return val ?? undefined;
  }
}
